import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import "./UpgradePreviewModal.scss";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  billing?: "monthly" | "yearly";
}

const getIntervalSuffix = (
  interval?: string,
  fallbackBilling?: "monthly" | "yearly",
) => {
  if (interval === "year" || interval === "yearly" || interval === "yr")
    return "/yr";
  if (interval === "month" || interval === "monthly" || interval === "mo")
    return "/mo";
  if (interval === "day" || interval === "daily") return "/day";
  if (interval === "week" || interval === "weekly") return "/wk";
  return fallbackBilling === "yearly" ? "/yr" : "/mo";
};

const UpgradePreviewModal: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  loading,
  billing,
}) => {
  const { billingUpgradePreviewData, billingUpgradePreviewLoading } =
    useSelector((state: RootState) => state.billing);

  const preview = billingUpgradePreviewData?.data;

  if (!preview) return null;

  const currentPlanSuffix = getIntervalSuffix(preview.currentPlan?.interval);
  const newPlanSuffix = getIntervalSuffix(preview.newPlan?.interval, billing);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          backgroundColor: "var(--background-color)",
          width: window.innerWidth > 768 ? "32%" : "100%",
          minWidth: { sm: "380px" },
          maxWidth: "480px",
          overflow: "hidden",
        },
      }}
    >
      {/* ===== HEADER ===== */}
      <DialogTitle className="upgrade-preview-title">
        <Box className="upgrade-title-header">
          <Typography variant="h6" className="heading">
            Upgrade Preview
          </Typography>

          <IconButton onClick={onClose} className="close-btn">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ===== CONTENT ===== */}
      <DialogContent className="upgrade-preview-container">
        {/* CURRENT PLAN */}
        <Box className="plan-row">
          <Typography className="label">Current Plan</Typography>
          <Typography className="value">
            {preview.currentPlan.name} – ${preview.currentPlan.price}
            {currentPlanSuffix}
          </Typography>
        </Box>

        {/* NEW PLAN */}
        <Box className="plan-row">
          <Typography className="label">New Plan</Typography>
          <Typography className="value highlight">
            {preview.newPlan.name} – ${preview.newPlan.price}
            {newPlanSuffix}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* AMOUNT */}
        <Box className="amount-box">
          <Typography className="label">Amount to Pay Now</Typography>
          <Typography className="amount">
            ${preview.amount}
          </Typography>
        </Box>

        {/* INFO FLAGS */}
        <Box className="info-box">
          {preview.willBeCharged && (
            <Typography className="success-text">
              ✔ You will be charged immediately
            </Typography>
          )}

          {preview.willReceiveCredit && (
            <Typography className="info-text">
              ✔ You will receive credits on your account
            </Typography>
          )}
        </Box>

        {/* DESCRIPTION */}
        {preview.description && (
          <Box className="description-box">
            <Typography className="description-text">
              {preview.description}
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* ===== ACTIONS ===== */}
      <Box className="upgrade-preview-actions">
        <Button onClick={onClose} variant="outlined"
          sx={{
            backgroundColor: 'var(--buttons-background-color)',
            border: 'var(--buttons-border)',
            width: 'auto',
            fontFamily: 'var(--font-family-manrope)',
            fontSize: 'var(--buttons-font-size, 16px)',
            borderRadius: 'var(--buttons-radius)',
            padding: 'var(--buttons-padding)',
            color: 'var(--buttons-font-color)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            textTransform: 'none',
            fontWeight: '400 !important',
            '&:hover': {
              color: 'var(--buttons-font-hover-color)',
              backgroundColor: 'var(--buttons-hover-background-color)',
              border: 'var(--buttons-hover-border)'
            }
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={onConfirm}
          disabled={billingUpgradePreviewLoading}
          loading={loading}
          sx={{
            width: 'auto',
            backgroundColor: 'var(--action-button-background-color, #1a73e8)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            padding: 'var(--action-button-small-padding)',
            borderRadius: 'var(--action-button-radius)',
            // borderRadius: "var(--buttons-radius, 6px)",
            fontFamily: 'var(--font-family-manrope)',
            fontSize: 'var(--buttons-font-size, 16px)',
            textTransform: 'none',
            transition: 'all 0.2s ease-in-out',
            fontWeight: '400 !important',
            '&.Mui-disabled': {
              opacity: 0.6,
              cursor: 'not-allowed'
            },
            '@media (max-width:480px)': {
              fontSize: '14px',
              padding: '8px 12px'
            }
          }}
        >
          Confirm & Pay
        </Button>
      </Box>
    </Dialog>
  );
};

export default UpgradePreviewModal;
